import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import './Login.css';
import Billede1 from '../../Images/Billede1.jpg';
import HeroSectionV2 from '../../components/HeroSectionV2';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { buildQueryParamsFromStorage } from '../../Helper/storageUtils';

const Login: React.FC = () => {
    const { language } = useLanguage();
    const location = useLocation();
    const navigate = useNavigate();

    const params = new URLSearchParams(location.search);
    const eventId = params.get('eventId');
    if (eventId) localStorage.setItem('eventId', eventId);

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [forgotMode, setForgotMode] = useState(false);

    const queryParams = new URLSearchParams(location.search).toString();

    // ===================== URL PARAM HANDLING =====================
    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const eventId = params.get("eventId");
        if (eventId) localStorage.setItem("eventId", eventId);
        else localStorage.removeItem("eventId");

        ["club", "media", "team"].forEach((p) => {
            if (params.has(p)) localStorage.setItem(p, "true");
            else localStorage.removeItem(p);
        });
    }, [location.search]);



    // ===================== LOGIN =====================
    const handleLogin = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage('');
        setInfoMessage('');

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/api/auth/login`,
                { email, password }
            );

            

            sessionStorage.setItem(
                "requireProfileUpdate",
                response.data.requireProfileUpdate === true ? "1" : "0"
            );


            // Save for header and logout
            const token = response.data.token || response.data.Token;
            const roles: string[] = response.data.roles || [];

            if (!token) {
                setErrorMessage("Login lykkedes, men token mangler.");
                return;
            }

            sessionStorage.setItem("authToken", token);

            const storedEventId = localStorage.getItem("eventId");
            const params = new URLSearchParams(location.search);
            const entry = params.get("entry");
            const eventId = params.get("eventId");

            let activeRoles = [...roles];

            if (entry?.toLowerCase() === "team") {
                activeRoles = ["Team"];
            }

            sessionStorage.setItem("userRoles", JSON.stringify(activeRoles));
            sessionStorage.setItem("userEmail", email);
            sessionStorage.setItem("username", String(response.data.username));
            sessionStorage.setItem("userId", String(response.data.userid));
            sessionStorage.setItem("personId", String(response.data.personid));

            window.dispatchEvent(new Event('auth-changed'));

            // 1) Highest priority: came from event selection (team/media/club/intern)
            if (entry && eventId) {
                const id = Number(eventId);

                switch (entry.toLowerCase()) {
                    case "team":
                        if (id) {
                            navigate(`/TeamDashboard/${id}`);
                        } else {
                            navigate("/Team");
                        }
                        return;
                    case "media":
                        navigate(`/MediaDashboard/${id}`);
                        return;
                    case "club":
                        navigate(`/ClubDashboard/${id}`);
                        return;
                    case "intern":
                        navigate(`/InternDashboard/${id}`);
                        return;
                    default:
                        navigate(`/EventPage/${id}`);
                        return;
                }
            }

            if (storedEventId) {
                navigate(`/EventPage/${storedEventId}`);
                return;
            }


            if (activeRoles.length > 1) {
                navigate("/role-select");
                return;
            }
            // Redirect based on role
            switch (activeRoles[0]) {
                case "Admin":
                    navigate("/Admin");
                    break;
                case "Club":
                    navigate("/Club");
                    break;
                case "Media":
                    navigate("/Media");
                    break;
                case "Team":
                    if (eventId) {
                        navigate(`/TeamDashboard/${eventId}`);
                    } else {
                        navigate("/Team");
                    }
                    return;
                default:
                    navigate("/UserDashboard");
            }
        } catch {
            setErrorMessage(
                language === 'en'
                    ? 'Incorrect email or password.'
                    : 'Forkert e-mail eller kodeord.'
            );
        }
    };

    // ===================== FORGOT PASSWORD =====================
    const handleForgotPassword = async () => {
        setErrorMessage('');
        setInfoMessage('');

        if (!email) {
            setErrorMessage(
                language === 'en'
                    ? 'Please enter your email.'
                    : 'Indtast venligst din e-mail.'
            );
            return;
        }

        try {
            await axios.post(
                `${process.env.REACT_APP_API_URL}/api/auth/forgot-password`,
                { email }
            );

            setInfoMessage(
                language === 'en'
                    ? 'If the email exists, a reset link has been sent.'
                    : 'Hvis e-mailen findes, er der sendt et link til nulstilling.'
            );
        } catch {
            setErrorMessage(
                language === 'en'
                    ? 'An error occurred. Please try again.'
                    : 'Der opstod en fejl. Prøv igen.'
            );
        }
    };

    // ===================== RENDER =====================
    return (
        <div className="home">
            <HeroSectionV2
                titleEn="Welcome to Badminton Denmark"
                titleDa="Velkommen til Badminton Danmark"
                descriptionEn="Login or sign up to access all the features!"
                descriptionDa="Log ind eller opret en profil for at få adgang til alle funktioner!"
                imageSrc={Billede1}
            />
            <section className="auth-section">
                <h1>
                    {language === 'en'
                        ? 'Login or Create an Account'
                        : 'Log ind eller Opret en Profil'}
                </h1>

                <div className="auth-boxes">
                    <div className="login-container">
                        <form
                            className="login-form"
                            onSubmit={forgotMode ? e => e.preventDefault() : handleLogin}
                        >
                            <div className="form-group">
                                <label>{language === 'en' ? 'Email' : 'E-mail'}</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                />
                            </div>

                            {!forgotMode && (
                                <div className="form-group">
                                    <label>{language === 'en' ? 'Password' : 'Kodeord'}</label>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={e => setPassword(e.target.value)}
                                        required
                                    />
                                </div>
                            )}

                            {forgotMode && (
                                <div className="login-info-box">
                                    {language === "en" ? (
                                        <>
                                            If you do not receive the reset email, please contact{" "}
                                            <a href="mailto:event@badminton.dk">event@badminton.dk</a>{" "}
                                            for help with your login.
                                        </>
                                    ) : (
                                        <>
                                            Hvis du ikke modtager nulstillingsmailen, kan du kontakte{" "}
                                            <a href="mailto:event@badminton.dk">event@badminton.dk</a>{" "}
                                            for hjælp til login.
                                        </>
                                    )}
                                </div>
                            )}

                            {!forgotMode ? (
                                <>
                                    <button type="submit" className="login-button">
                                        {language === 'en' ? 'Login' : 'Log ind'}
                                    </button>

                                    <button
                                        type="button"
                                        className="forgot-button"
                                        onClick={() => {
                                            setForgotMode(true);
                                            setErrorMessage('');
                                            setInfoMessage('');
                                        }}
                                    >
                                        {language === 'en'
                                            ? 'Forgot password?'
                                            : 'Glemt kodeord?'}
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="login-button"
                                        onClick={handleForgotPassword}
                                    >
                                        {language === 'en'
                                            ? 'Send reset link'
                                            : 'Send nulstillingslink'}
                                    </button>

                                    <button
                                        type="button"
                                        className="forgot-button secondary"
                                        onClick={() => {
                                            setForgotMode(false);
                                            setPassword('');
                                            setErrorMessage('');
                                            setInfoMessage('');
                                        }}
                                    >
                                        {language === 'en'
                                            ? 'Back to login'
                                            : 'Tilbage til login'}
                                    </button>
                                </>
                            )}
                        </form>

                        {errorMessage && <p className="error">{errorMessage}</p>}
                        {infoMessage && <p className="success">{infoMessage}</p>}
                    </div>

                    <div className="auth-divider">
                        <span>{language === 'en' ? 'Or' : 'Eller'}</span>
                    </div>

                    <div className="register-container">
                        <h2>{language === 'en' ? 'Sign Up' : 'Opret Profil'}</h2>
                        <button
                            className="login-button"
                            onClick={() => navigate(`/register?${queryParams}`)}
                        >
                            {language === 'en' ? 'Sign Up' : 'Opret Profil'}
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Login;